import { authManager } from "@/lib/managers/auth-manager";
import { convertUtcDateValueToDateWithoutTimezone } from "@/lib/utils/date-2";
import type { TimeZonesMapValues } from "@/lib/utils/timezones";
import {
   TIME_ZONES_MAP_REVERSED,
   timeOptions,
   timeZoneOptions,
   timeZoneSearchComparator,
} from "@/lib/utils/timezones";
import { getProcoreCompanyIdFromURL } from "@/lib/utils/url";
import { ColorPicker } from "@/react/components/common/components/color-picker";
import type {
   ExtendedFieldType,
   IntegratedField,
   StreamResponseConversion,
   Tab,
} from "@/react/prop-types";
import { EntityAbbreviation, FieldType, TearsheetTabs } from "@/react/prop-types";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { CustomTabs } from "@/react/shared/components/custom-tabs";
import { QRCodeCard } from "@/react/shared/components/qr-code-card";
import { PROJECT_STATUS_OPTIONS } from "@/react/shared/constants";
import { processCustomFieldsFormValues } from "@/react/shared/custom-field-utils";
import { getFilteredFields, isSensitiveFieldEditable } from "@/react/shared/helper";
import { ProjectStore } from "@/stores/project-store.core";
import {
   CustomFieldType,
   ProjectStatus,
} from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import type { UpdateSingleProjectResponse } from "@laborchart-modules/lc-core-api/dist/api/projects/update-projects";
import { Lock } from "@procore/core-icons";
import {
   Box,
   Button,
   Card,
   Flex,
   FlexList,
   Form,
   H1,
   H2,
   Page,
   Required,
   Spinner,
   Token,
   Tooltip,
   Typography,
   useI18nContext,
} from "@procore/core-react";
import type { OptionItem } from "@procore/core-react/dist/MultiSelect/MultiSelect.types";
import { useQueryClient } from "@tanstack/react-query";
import { type FormikHelpers, type FormikProps } from "formik";
import React, { useEffect, useState } from "react";
import { useGetProcoreEnvironment } from "../../common/queries/procore-queries";
import { useGetCompany, useGetGroupOptionsQuery } from "../../common/queries/queries";
import { FieldMapping } from "../../project-list/project-list-enums";
import { DetailsAttachmentsCard } from "../attachments/details-attachments-card";
import { DetailsNotesCard } from "../notes/details-notes-card";
import { NotesType } from "../notes/types";
import { PhotoUploader } from "../shared/photo-uploader";
import { DetailsTagsCard } from "../tags/details-tags-card";
import { PageType } from "../types";
import { renderLinkToProcoreEntity } from "../utils";
import {
   projectDetailFieldsSection1,
   projectDetailFieldsSection2,
   projectDetailFieldsSection3,
   projectDetailFieldsSection3WithStage,
} from "./constants";
import { buildProjectUpdatePayload } from "./helpers";
import { CategoriesCard } from "./categories/categories-card";
import LaunchDarklyClient from "@laborchart-modules/launch-darkly-browser";
import { LaborGraphCard } from "./labor-graph-card";
import { ProjectDetailsActivities } from "./project-detail-cards/project-details-activities-card";
import { ProjectCustomAssignmentAlerts } from "./project-detail-cards/project-details-custom-assignment-alerts";
import { ProjectDetailsRolesCard } from "./project-detail-cards/project-details-roles-card";
import { ProjectDetailsWageOverridesCard } from "./project-detail-cards/project-details-wage-overrides-card";
import { useGetProjectQuery } from "./queries";
import type { ExtendedProjectDetail, ProjectSpecificationsFormValues } from "./types";
import {
   type ExtendedGetProjectDetailResponse,
   type ProjectDetailFormValues,
   type ProjectDetailTearsheetProps,
   type ProjectDetailsFields,
} from "./types";
import { projectDetailSchema, projectSpecificationsSchema } from "./validation-schemas";

//adding more objects to this array will automatically start rendering other tabs, just maintain unique ID and labels.
const PROJECT_DETAILS_TABS: Tab[] = [
   { id: 0, label: TearsheetTabs.OVERVIEW, visible: true },
   { id: 1, label: TearsheetTabs.DETAILS, visible: true },
   { id: 2, label: TearsheetTabs.NOTES, visible: true },
   { id: 3, label: TearsheetTabs.ACTIVITY, visible: true },
];

export const ProjectDetailTearsheet: React.FC<ProjectDetailTearsheetProps> = ({
   projectId,
   handleCreateLaborPlanClick,
   onUpdateProject,
   integratedFields,
   customFields,
   showActiveTab,
   callback,
}) => {
   const I18n = useI18nContext();

   const selectAllOption = [
      {
         id: "select_all",
         label: I18n.t("views.company.workforce_planning.select_all"),
      },
   ];
   const [formViewMode, setFormViewMode] = React.useState<"read" | "update">("read");
   const queryClient = useQueryClient();
   const {
      data: projectData,
      isLoading: projectDataLoading,
      isFetching: projectDataFetching,
      refetch,
   } = useGetProjectQuery(projectId);
   const showProjectStage = projectData?.data.cornerstone_stage_name;
   const { data: groupOptions, isLoading: groupOptionsLoading } = useGetGroupOptionsQuery();
   const { data: companyData } = useGetCompany();
   const { data: procoreEnvironment } = useGetProcoreEnvironment();
   const validationSchema = projectDetailSchema(I18n, customFields);
   const validationSchemaSpecifications = projectSpecificationsSchema();
   const dataIsLoading = projectDataLoading || groupOptionsLoading || projectDataFetching;
   const [projectIntegratedFields, setProjectIntegratedFields] = useState<IntegratedField[]>([]);
   let initialValues: ProjectDetailFormValues | undefined;
   const procoreCompanyId: string | undefined = getProcoreCompanyIdFromURL();

   const activeTabId =
      (showActiveTab && PROJECT_DETAILS_TABS.find((tab) => tab.label === showActiveTab)?.id) ?? 0;
   const [activeTab, setActiveTab] = useState(activeTabId || 0);
   const [canViewProjectWageOverrides, setCanViewProjectWageOverrides] =
      React.useState<boolean>(false);
   const [canEditProjectDetails, setCanEditProjectDetails] = React.useState<boolean>(false);
   const { checkAuthAction } = usePermissionContext();
   const [qrCodeEnabled, setQrCodeEnabled] = React.useState<boolean | undefined>(false);
   const [projectQrCodeEnabled, setProjectQRCodeEnabled] = React.useState<boolean | undefined>(
      false,
   );
   const [canViewProjectTags, setCanViewProjectTags] = useState<boolean>(false);
   const [canViewProjectAttachments, setCanViewProjectAttachments] = useState<boolean>(false);
   const [canEditProjectCategories, setCanEditProjectCategories] = useState<boolean>(false);
   const [projectDetailTabsState, setProjectDetailTabsState] = useState(PROJECT_DETAILS_TABS);

   const [canViewProjectSensitiveFields, setCanViewProjectSensitiveFields] = useState(false);
   const [canEditProjectSensitiveFields, setCanEditProjectSensitiveFields] = useState(false);
   const [projectSensitiveFields, setProjectSensitiveFields] = useState<string[]>([]);
   const [canViewProjectFinancials, setCanViewProjectFinancials] = useState(false);
   const [canViewAssignments, setCanViewAssignments] = useState(false);

   useEffect(() => {
      checkUserCanViewTab();
      const canViewProjectWageOverrides = checkAuthAction(AuthAction.VIEW_PROJECT_WAGE_OVERRIDES);
      const canEditProjectDetails = checkAuthAction(AuthAction.EDIT_PROJECT_DETAILS);
      const canViewProjectTags = checkAuthAction(AuthAction.VIEW_PROJECT_TAGS);
      const canViewProjectAttachments = checkAuthAction(AuthAction.VIEW_PROJECT_ATTACHMENTS);
      const canViewAssignments = checkAuthAction(AuthAction.VIEW_ASSIGNMENTS);
      const canEditProjectCategories = checkAuthAction(AuthAction.EDIT_PROJECT_CATEGORIES);
      setCanViewProjectWageOverrides(canViewProjectWageOverrides);
      setCanEditProjectDetails(canEditProjectDetails);
      setCanViewProjectTags(canViewProjectTags);
      setCanViewProjectAttachments(canViewProjectAttachments);
      setCanEditProjectCategories(canEditProjectCategories);
      setQrCodeEnabled(authManager.companyModules()?.qrCodes);
      setProjectQRCodeEnabled(authManager.companyModules()?.projectsQrCodes);
      setCanViewAssignments(canViewAssignments);

      const canViewProjectSensitiveFields = checkAuthAction(AuthAction.VIEW_PROJECT_SENSITIVE);
      setCanViewProjectSensitiveFields(canViewProjectSensitiveFields);

      const canEditProjectSensitiveFields = checkAuthAction(AuthAction.EDIT_PROJECT_SENSITIVE);
      setCanEditProjectSensitiveFields(canEditProjectSensitiveFields);

      setProjectSensitiveFields(authManager.projectsSensitiveFields());

      const canViewProjectFinancials = checkAuthAction(AuthAction.VIEW_PROJECT_FINANCIALS);
      setCanViewProjectFinancials(canViewProjectFinancials);
   }, []);

   useEffect(() => {
      if (integratedFields) {
         if (Array.isArray(integratedFields.data.projects_integrated_fields)) {
            setProjectIntegratedFields(integratedFields.data.projects_integrated_fields);
         }
      }
   }, [integratedFields]);

   const checkUserCanViewTab = () => {
      const newProjectDetailTabsState = [...projectDetailTabsState];
      newProjectDetailTabsState[2].visible = checkAuthAction(AuthAction.VIEW_PROJECT_NOTES);
      newProjectDetailTabsState[3].visible = checkAuthAction(AuthAction.VIEW_PROJECT_ACTIVITY);
      setProjectDetailTabsState(newProjectDetailTabsState);
   };

   const PROJECT_STATUS_MAP = {
      pending: {
         id: ProjectStatus.PENDING,
         label: I18n.t("views.company.workforce_planning.pending"),
         color: "yellow",
      },
      active: {
         id: ProjectStatus.ACTIVE,
         label: I18n.t("views.company.workforce_planning.active"),
         color: "green",
      },
      inactive: {
         id: ProjectStatus.INACTIVE,
         label: I18n.t("views.company.workforce_planning.inactive"),
         color: "gray",
      },
   } as const;

   // Function to generate custom label for fields
   function generateCustomLabel(field: string, translationString: string): JSX.Element {
      return (
         <div style={{ display: "inline-flex", alignItems: "center" }}>
            <span>{I18n.t(translationString)}</span>
            {isIntegratedField(field) && <Lock size="sm" style={{ marginLeft: "3px" }} />}
         </div>
      );
   }

   function getFormValues(
      data: ExtendedGetProjectDetailResponse["data"] | UpdateSingleProjectResponse["data"],
      groupOptions: StreamResponseConversion,
   ) {
      const baseFormValues = {
         name: data.name,
         job_number: data.job_number,
         status: PROJECT_STATUS_MAP[data.status],
         group_ids: (data.group_ids ?? [])
            .map((groupId) => groupOptions?.find(({ id }) => id === groupId))
            .filter((group) => group !== undefined),
         start_date: convertUtcDateValueToDateWithoutTimezone(data.start_date),
         est_end_date: convertUtcDateValueToDateWithoutTimezone(data.est_end_date),
         daily_start_time: {
            id: data.daily_start_time,
            label: timeOptions.find(({ id }) => id === data.daily_start_time)!.label,
         },
         daily_end_time: {
            id: data.daily_end_time,
            label: timeOptions.find(({ id }) => id === data.daily_end_time)!.label,
         },
         timezone: timeZoneOptions.find(
            ({ id }) => id === TIME_ZONES_MAP_REVERSED[(data.timezone ?? "") as TimeZonesMapValues],
         ),
         address_1: data.address_1 || null,
         address_2: data.address_2 || null,
         city_town: data.city_town || null,
         state_province: data.state_province || null,
         zipcode: data.zipcode || null,
         country: data.country || null,
         bid_rate: data.bid_rate || null,
         color: data.color,
         customer_name: data.customer_name || null,
         percent_complete: data.percent_complete || null,
         project_type: data.project_type || null,
         cornerstone_stage_name: (data as ExtendedProjectDetail).cornerstone_stage_name || null,
      };

      // Initialize custom fields in form values
      const customFieldValues = processCustomFieldsFormValues(data.custom_fields);
      return { ...baseFormValues, ...customFieldValues };
   }

   /*Function to check if the field is an integrated field and is locked in case of custom fields property matches with id
   and for common fields it matches with name*/
   function isIntegratedField(field: string) {
      return projectIntegratedFields.some(
         (integratedField: IntegratedField) =>
            integratedField.property === field && integratedField.locked,
      );
   }

   // Function to render form fields
   function renderFormFields(fields: ProjectDetailsFields[], fieldSource?: string) {
      const rows = [];

      // Filter out sensitive fields if user does not have permission to view them OR type is currency and user does not have permission to view financials
      const filteredFields = getFilteredFields(
         fields,
         projectSensitiveFields,
         canViewProjectSensitiveFields,
         canViewProjectFinancials,
      );

      for (let i = 0; i < filteredFields.length; i += 3) {
         const rowFields = filteredFields.slice(i, i + 3);

         const validComponents = rowFields
            .map((field: ProjectDetailsFields, index: number) => {
               let FormComponent = null;
               let additionalProps = {};
               let colStartBase: 1 | 5 | 9;
               //logic for handling the column start base in the form field
               switch (index) {
                  case 0:
                     colStartBase = 1;
                     break;
                  case 1:
                     colStartBase = 5;
                     break;
                  default:
                     colStartBase = 9;
                     break;
               }
               if (fieldSource === "project") {
                  if (field.name === FieldMapping.GROUPS)
                     field.values = selectAllOption.concat(groupOptions ?? []) as any;
                  if (field.name === FieldMapping.STATUS) field.values = PROJECT_STATUS_OPTIONS;
                  if (
                     field.name === FieldMapping.DAILY_START_TIME ||
                     field.name === FieldMapping.DAILY_END_TIME
                  )
                     field.values = timeOptions;
                  if (field.name === FieldMapping.TIMEZONE) field.values = timeZoneOptions;
               }

               switch (field.type as ExtendedFieldType & "photo") {
                  case "photo":
                     return (
                        <Form.Field
                           key={"project-field-photo"}
                           name="photo"
                           colStart={colStartBase}
                           colWidth={4}
                        >
                           <Typography
                              as="span"
                              weight="semibold"
                              style={{
                                 gridRow: 1,
                                 gridColumnStart: colStartBase,
                                 gridColumnEnd: colStartBase + 4,
                              }}
                           >
                              {I18n.t("views.company.workforce_planning.projects.project_photo")}
                           </Typography>
                           <PhotoUploader
                              imageIdOrUrl={projectData?.data.profile_pic_url ?? ""}
                              onUploadPhoto={handleUploadPhoto}
                              disableUpload={!canEditProjectDetails}
                              style={{
                                 gridColumnStart: colStartBase,
                                 gridColumnEnd: colStartBase + 4,
                              }}
                           />
                        </Form.Field>
                     );
                  case CustomFieldType.DATE:
                     FormComponent = Form.DateSelect;
                     break;
                  case CustomFieldType.NUMBER:
                     FormComponent = Form.Number;
                     break;
                  case CustomFieldType.TEXT:
                     FormComponent = Form.Text;
                     additionalProps = {
                        ...(field.name === FieldMapping.CORNERSTONE_STAGE_NAME && {
                           disabled: true,
                           style: {
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              display: "block",
                              wordBreak: "break-all",
                           },
                        }),
                     };
                     break;
                  case CustomFieldType.SELECT:
                     FormComponent = Form.Select;

                     additionalProps = {
                        options:
                           fieldSource === "project"
                              ? field.values
                              : field.values?.map((value: string) => ({
                                   id: field.name + "_" + value,
                                   label: value,
                                })) || [],
                        onSearch: false,
                        ...(field.name === "timezone" && {
                           searchComparator: timeZoneSearchComparator,
                        }),
                        placeholder: I18n.t("views.company.workforce_planning.select_option"),
                     };
                     break;
                  case CustomFieldType.MULTI_SELECT:
                     FormComponent = Form.MultiSelect;
                     additionalProps = {
                        options:
                           fieldSource === "project"
                              ? field.values
                              : field.values?.map((value: string) => ({
                                   id: field.name + "_" + value,
                                   label: value,
                                })) || [],
                        placeholder: I18n.t("views.company.workforce_planning.select_option"),
                        ...(field.name === FieldMapping.GROUPS && {
                           tokenRenderer: ({
                              disabled,
                              focused,
                              getLabel,
                              option,
                              removeToken,
                           }: {
                              disabled: boolean;
                              focused: boolean;
                              getLabel: (opt: OptionItem) => React.ReactNode;
                              option: any;
                              removeToken: () => void;
                           }) => (
                              <Tooltip overlay={getLabel(option)} trigger="hover">
                                 <Token disabled={disabled} focused={focused}>
                                    <Token.Label>
                                       {option.id === "select_all"
                                          ? I18n.t("views.company.workforce_planning.all_groups")
                                          : getLabel(option)}
                                    </Token.Label>
                                    <Token.Remove data-close onClick={removeToken} />
                                 </Token>
                              </Tooltip>
                           ),
                        }),
                     };
                     break;
                  case CustomFieldType.PARAGRAPH:
                     FormComponent = Form.TextArea;
                     break;
                  case CustomFieldType.BOOL:
                     FormComponent = Form.Checkbox;
                     break;
                  case CustomFieldType.CURRENCY:
                     FormComponent = Form.Currency;
                     additionalProps = {
                        prefix: procoreEnvironment?.currency_symbol ?? "$",
                     };
                     break;
                  case CustomFieldType.HEX_COLOR:
                     FormComponent = Form.Field;
                     additionalProps = {
                        as: ColorPicker,
                     };
                     break;
                  case FieldType.PILL_SELECT:
                     FormComponent = Form.PillSelect;
                     additionalProps = {
                        options:
                           field.values?.map((value) => ({
                              ...value,
                              label: I18n.t(value.label),
                           })) || [],
                     };
                     break;
               }

               if (!FormComponent) return null; // If FormComponent is null, return null

               // Set disabled true if field is an integrated field and is locked
               // in case of custom fields property matches with id and for common fields it matches with name
               if (
                  isIntegratedField(fieldSource === "project" ? field.name : field.id) ||
                  field.integration_only ||
                  isSensitiveFieldEditable(
                     field.name,
                     projectSensitiveFields,
                     canEditProjectSensitiveFields,
                  )
               ) {
                  additionalProps = { ...additionalProps, disabled: true };
               }

               return (
                  <React.Fragment
                     key={
                        fieldSource === "project"
                           ? `project-field-fragment-outside-${field.name}`
                           : `custom-field-fragment-outside-${field.id}-${index}`
                     }
                  >
                     <FormComponent
                        key={
                           fieldSource === "project"
                              ? `project-field-${field.name}`
                              : `custom-field-${field.id}`
                        }
                        colStart={colStartBase}
                        colWidth={4}
                        name={field.name}
                        // @ts-expect-error: passing element as needed to append lock icon
                        label={
                           fieldSource === "project" ? (
                              generateCustomLabel(field.name, field.id)
                           ) : (
                              <div style={{ display: "flex", alignItems: "center" }}>
                                 <span>{field.name}</span>
                                 {(isIntegratedField(field.id) || field.integration_only) && (
                                    <Lock size="sm" style={{ marginLeft: "3px" }} />
                                 )}
                              </div>
                           )
                        }
                        {...additionalProps}
                        required={field.required}
                     />
                  </React.Fragment>
               );
            })
            .filter((component: any) => component !== null); // Filter out null components

         if (validComponents.length > 0) {
            rows.push(<Form.Row key={`custom-fields-row-${i}`}>{validComponents}</Form.Row>);
         }
      }
      return rows;
   }

   if (projectData?.data) {
      const groups = groupOptions ?? [];
      initialValues = getFormValues(projectData.data, groups);
   }

   async function handleSubmit(
      values: ProjectDetailFormValues,
      { setValues, resetForm }: FormikHelpers<ProjectDetailFormValues>,
   ) {
      const payload = buildProjectUpdatePayload({
         values,
         initialValues,
         groupOptions: groupOptions!,
         integratedFields: projectIntegratedFields,
         customFields,
         canEditProjectSensitiveFields,
         projectSensitiveFields,
         canViewProjectFinancials,
      });

      try {
         const updatedProject = await updateProjectData(payload);
         if (updatedProject.data && groupOptions) {
            refetch();
            setValues(getFormValues(updatedProject.data, groupOptions));
            setFormViewMode("read");
            onUpdateProject();
         }

         // Handle callback method to update the Bryntum Gantt
         if (callback) {
            callback({ updatedProject: updatedProject.data });
         }
      } catch {
         // will rollback changes by resetting form for now
         resetForm();
      }
   }

   async function updateProjectData(payload: any) {
      return await ProjectStore.updateProject(projectId, payload).payload;
   }

   /* istanbul ignore next */
   async function handleUploadPhoto(url: string | null) {
      await updateProjectData({ profile_pic_url: url });
      queryClient.setQueryData(
         ["project", projectId],
         (old: ExtendedGetProjectDetailResponse | undefined) =>
            !old?.data ? old : { ...old, data: { ...old.data, profile_pic_url: url } },
      );
   }

   async function handleSubmitSpecifications(
      values: ProjectSpecificationsFormValues,
      { resetForm }: FormikHelpers<ProjectSpecificationsFormValues>,
   ) {
      const payload: any = {
         default_recipient_ids: values.default_recipient_ids,
      };

      try {
         const updatedProject = await ProjectStore.updateProject(projectId, payload).payload;
         if (updatedProject.data) {
            setFormViewMode("read");
            refetch();
         }
      } catch {
         // will rollback changes by resetting form for now
         resetForm();
      }
   }

   return (
      <Page>
         <Page.Main
            style={{
               width: "952px",
               display: "flex",
               flexDirection: "column",
            }}
         >
            <Spinner loading={dataIsLoading}>
               <Page.Header>
                  <Page.Title>
                     <H1>
                        {projectData?.data.name}{" "}
                        {renderLinkToProcoreEntity({
                           entityType: CustomFieldEntity.PROJECT,
                           procoreEntityId: projectData?.data.procore_id,
                           procoreCompanyId: procoreCompanyId,
                        })}
                     </H1>
                  </Page.Title>
                  <CustomTabs
                     tabs={projectDetailTabsState}
                     activeTab={activeTab}
                     setActiveTab={setActiveTab}
                  />
               </Page.Header>
               {/** Details Tab */}
               {activeTab === projectDetailTabsState[1].id && (
                  <Form
                     view={formViewMode}
                     validationSchema={validationSchemaSpecifications}
                     onSubmit={handleSubmitSpecifications}
                  >
                     {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
                     {({
                        submitForm,
                        isSubmitting,
                        handleChange,
                     }: FormikProps<ProjectDetailFormValues>) => (
                        <>
                           <Page.Body>
                              <Flex direction="column" gap="5px" paddingRight="sm">
                                 {canViewProjectTags && (
                                    <DetailsTagsCard
                                       detailsData={projectData ?? null}
                                       refreshTable={onUpdateProject}
                                       refetchData={refetch}
                                       loading={projectDataLoading || projectDataFetching}
                                       updateData={updateProjectData}
                                       pageType={PageType.PROJECT}
                                    />
                                 )}
                                 {canViewProjectAttachments && (
                                    <DetailsAttachmentsCard
                                       attachmentData={projectData ?? null}
                                       refetchData={refetch}
                                       loading={projectDataLoading || projectDataFetching}
                                       updateData={updateProjectData}
                                       pageType={PageType.PROJECT}
                                    />
                                 )}
                                 {canEditProjectCategories &&
                                    LaunchDarklyClient.getFlagValue("project-categories-manager") &&
                                    projectData?.data.id && (
                                       <CategoriesCard projectId={projectData.data.id} />
                                    )}
                                 {canViewProjectWageOverrides && (
                                    <ProjectDetailsWageOverridesCard
                                       detailsData={projectData ?? null}
                                       refreshTable={onUpdateProject}
                                       refetchData={refetch}
                                       loading={projectDataLoading || projectDataFetching}
                                       updateData={updateProjectData}
                                       pageType={PageType.PROJECT}
                                    />
                                 )}
                                 {canViewAssignments && (
                                    <ProjectCustomAssignmentAlerts
                                       projectData={projectData ?? null}
                                       editMode={formViewMode === "update"}
                                       setEditMode={() => setFormViewMode("update")}
                                       handleChange={handleChange}
                                       loading={projectDataLoading || projectDataFetching}
                                       refetchData={refetch}
                                    />
                                 )}
                                 {qrCodeEnabled && projectQrCodeEnabled && (
                                    <QRCodeCard
                                       entityAbbreviation={EntityAbbreviation.PROJECT}
                                       companyQrId={companyData?.data.qr_id}
                                       entityQrId={projectData?.data.qr_id}
                                       entityTitle={projectData?.data.name}
                                       entitySubtitle={projectData?.data.job_number}
                                       imageDetailsHelpText={I18n.t(
                                          "views.company.workforce_planning.projects.qr_code.image_details_help_text",
                                       )}
                                    />
                                 )}
                              </Flex>
                           </Page.Body>
                           {formViewMode === "update" && (
                              <Page.Footer data-testid="specification-tab-footer">
                                 <Box padding="md">
                                    <Flex justifyContent="space-between" alignItems="center">
                                       <Required showLabel />
                                       <FlexList space="sm">
                                          <Button
                                             variant="tertiary"
                                             onClick={() => {
                                                setFormViewMode("read");
                                             }}
                                          >
                                             {I18n.t("views.company.workforce_planning.cancel")}
                                          </Button>
                                          <Button
                                             type="submit"
                                             disabled={isSubmitting}
                                             onClick={submitForm}
                                          >
                                             {I18n.t("views.company.workforce_planning.save")}
                                          </Button>
                                       </FlexList>
                                    </Flex>
                                 </Box>
                              </Page.Footer>
                           )}
                        </>
                     )}
                  </Form>
               )}
               {/** Notes Tab */}
               {activeTab === projectDetailTabsState[2].id && (
                  <Page.Body style={{ width: "100%", marginBottom: "auto" }}>
                     <DetailsNotesCard
                        refetchData={refetch}
                        loading={projectDataLoading}
                        noteType={NotesType.PROJECT}
                        notesData={projectData}
                        contextId={projectId}
                     />
                  </Page.Body>
               )}
               {/** Activity Tab */}
               {activeTab === projectDetailTabsState[3].id && (
                  <Page.Body style={{ width: "100%", marginBottom: "auto" }}>
                     <ProjectDetailsActivities projectId={projectId} />
                  </Page.Body>
               )}
               {/** Overview Tab */}
               {activeTab === projectDetailTabsState[0].id && (
                  <Form
                     view={formViewMode}
                     initialValues={initialValues}
                     enableReinitialize
                     validationSchema={validationSchema}
                     onSubmit={handleSubmit}
                  >
                     {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
                     {({
                        submitForm,
                        isSubmitting,
                        resetForm,
                     }: FormikProps<ProjectDetailFormValues>) => (
                        <>
                           <Page.Body>
                              <LaborGraphCard
                                 projectId={projectId}
                                 projectName={projectData?.data.name ?? ""}
                                 handleCreateLaborPlanClick={handleCreateLaborPlanClick}
                                 callback={callback}
                              />
                              <Card style={{ marginBottom: "5px" }}>
                                 <Form.Form>
                                    <FlexList justifyContent="space-between" padding="md">
                                       <H2>
                                          {I18n.t("views.company.workforce_planning.information")}
                                       </H2>
                                       {formViewMode === "read" && canEditProjectDetails && (
                                          <Button
                                             variant="secondary"
                                             onClick={() => setFormViewMode("update")}
                                             data-testid="edit-project-details"
                                          >
                                             {I18n.t("views.company.workforce_planning.edit")}
                                          </Button>
                                       )}
                                    </FlexList>
                                    <Box padding="md">
                                       {projectDetailFieldsSection1 && (
                                          <>
                                             {" "}
                                             {renderFormFields(
                                                projectDetailFieldsSection1 as ProjectDetailsFields[],
                                                "project",
                                             )}{" "}
                                          </>
                                       )}
                                       <hr />
                                       {projectDetailFieldsSection2 && (
                                          <>
                                             {" "}
                                             {renderFormFields(
                                                projectDetailFieldsSection2 as ProjectDetailsFields[],
                                                "project",
                                             )}{" "}
                                          </>
                                       )}
                                       <hr />
                                       {projectDetailFieldsSection3 && (
                                          <>
                                             {" "}
                                             {renderFormFields(
                                                showProjectStage
                                                   ? (projectDetailFieldsSection3WithStage as ProjectDetailsFields[])
                                                   : (projectDetailFieldsSection3 as ProjectDetailsFields[]),
                                                "project",
                                             )}{" "}
                                          </>
                                       )}

                                       {customFields && (
                                          <>
                                             <hr />
                                             {renderFormFields(
                                                customFields as ProjectDetailsFields[],
                                             )}
                                          </>
                                       )}
                                    </Box>
                                 </Form.Form>
                              </Card>
                              <ProjectDetailsRolesCard
                                 detailsData={projectData ?? null}
                                 refetchData={refetch}
                                 updateData={updateProjectData}
                                 loading={projectDataLoading}
                                 pageType={PageType.PROJECT}
                                 refreshTable={onUpdateProject}
                              />
                           </Page.Body>
                           {formViewMode === "update" && (
                              <Page.Footer>
                                 <Flex
                                    justifyContent="space-between"
                                    alignItems="center"
                                    padding="md"
                                 >
                                    <Required showLabel />
                                    <FlexList space="sm">
                                       <Button
                                          variant="tertiary"
                                          disabled={isSubmitting}
                                          onClick={() => {
                                             setFormViewMode("read");
                                             resetForm();
                                          }}
                                       >
                                          {I18n.t("views.company.workforce_planning.cancel")}
                                       </Button>
                                       <Button
                                          type="submit"
                                          disabled={isSubmitting}
                                          onClick={submitForm}
                                       >
                                          {I18n.t("views.company.workforce_planning.save")}
                                       </Button>
                                    </FlexList>
                                 </Flex>
                              </Page.Footer>
                           )}
                        </>
                     )}
                  </Form>
               )}
            </Spinner>
         </Page.Main>
      </Page>
   );
};
